import api from './index'
import type { ApiResponse, V1ApiResponse } from '@/types'

export interface MembershipGrantSchedule {
  membershipGrantScheduleId: number
  periodNo: number
  scheduledDateTime: string
  grantAmount: string
  grantMethod: number
  grantStatus: number
  grantDateTime: string | null
}

export interface UserMembership {
  userMembershipId: number
  membershipNo: string
  membershipPlanCode: string
  membershipPlanName: string
  membershipLevelCode: string
  membershipLevelName: string
  membershipLevelRank: number
  planType: number
  sourceType: number
  paidAmount: string
  membershipStatus: number
  effectiveDateTime: string
  expireDateTime: string
  remainingSeconds: number
  frozenDateTime: string | null
  schedules: MembershipGrantSchedule[]
}

export interface UserMembershipsData {
  list: UserMembership[]
}

export interface FreeClaimEligibility {
  canClaim: boolean
  reasonCode: string
  reason: string
  serverDateTime: string
  membershipPlanCode: string
  userMembershipId: number | null
  claimableScheduleId: number | null
  claimablePeriodNo: number | null
  claimableDateTime: string | null
  nextClaimDateTime: string | null
}

export interface ClaimFreeMembershipData {
  membership: UserMembership
  granted: MembershipGrantSchedule | null
}

export interface MembershipPlanProduct {
  productId: number
  productCode: string
  productName: string
  productKind: string
  productPrice: string
  productDiscount?: string
  productPayPrice: string
  productDesc: string
}

export interface MembershipPlanBenefit {
  membershipBenefitId: number
  benefitCode: string
  benefitName: string
  benefitType: number
  benefitValue: string | null
  valueUnit: string | null
  displayText: string
  iconUrl: string | null
  description: string | null
  sort: number
}

export interface MembershipPlan {
  membershipPlanId: number
  planCode: string
  planName: string
  membershipLevelCode: string
  membershipLevelName: string
  membershipLevelRank: number
  planType: number
  durationUnit: number
  durationValue: number
  grantMode: number
  grantCount: number
  grantAmount: string
  walletCategoryCode: string | null
  description: string | null
  sort: number
  products: MembershipPlanProduct[]
  benefits: MembershipPlanBenefit[]
}

export interface MembershipPlansData {
  list: MembershipPlan[]
}

// 会员API接口
export const membershipApi = {
  // 会员列表
  vipInfoList: (params?: any): Promise<ApiResponse<any>> =>
    api.get('/api-user/vipItem/list', { params }),

  // 会员升级提示（用于未购会员前判断是否展示升级弹窗）
  vipTip: (params?: any): Promise<ApiResponse<any>> => api.get('/api-user/vip/tip', { params }),

  // 协议
  getPolicy: (params: any): Promise<ApiResponse<any>> =>
    api.get('/api-user/userVip/findContentByCode', { params }),

  // 权益详细信息
  vipRightsInfo: (): Promise<ApiResponse<any>> => api.get('/api-user/vipRights/list'),

  // 潮币购买产品
  waveCoinList: (params: any): Promise<ApiResponse<any>> =>
    api.get('/api-user/waveCoin/list', { params }),

  /** 查询启用的会员套餐及绑定商品，无需登录 */
  queryPlans: (): Promise<V1ApiResponse<MembershipPlansData>> =>
    api.get('/v1/memberships/plans', { noAuthRedirect: true } as any),

  /** 查询当前用户会员实例和发放计划，无需传递查询参数 */
  queryMine: (): Promise<V1ApiResponse<UserMembershipsData>> =>
    api.get('/v1/users/me/memberships'),

  /** 查询当前用户是否可以领取免费会员权益 */
  queryFreeClaimEligibility: (): Promise<V1ApiResponse<FreeClaimEligibility>> =>
    api.get('/v1/users/me/memberships/free-claim-eligibility'),

  /** 领取当前免费会员期次 */
  claimFree: (): Promise<V1ApiResponse<ClaimFreeMembershipData>> =>
    api.post('/v1/users/me/memberships/free-claim'),
}

export default membershipApi
